function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * A query-builder component defines a step like query, consisting of
 * selection of events and exclusion steps
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import findKey from 'lodash/findKey';
import clsx from 'clsx';
import omit from 'lodash/omit';
import { MetricType } from 'common/types/reports/bookmark';
import { getReportUrl } from 'common/report/util';
import { dataGroupIDsEqual } from 'common/util';
import { mpColors } from 'common/util/colors';
import { emitTrackEvent } from 'common/util/dom';
import { DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { CountUsersOnceState, ReportName, ResourceType, SPECIAL_ALL_EVENT_VALUES } from 'common/report/constants';
import { Entry, EntrySectionUpdates, EntryType, FiltersOperator, GroupEventBehaviors, isEventBasedEntry, isFormulaEntry, isGroupedEntry, isMetricEntry, isUserBasedEntry, MetricEntry, QueryEntry, QueryEntrySectionProps, QueryEventEntry, QueryFilterEntry, QuerySegmentEntry, RowLabelTheme, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { customEventsClient } from 'common/report/queries/app-api/custom-events-client';
import { CustomEventDefinition } from 'common/report/util/saved-entities/types';
import { QueryReason } from 'common/report/util/query-tracking/types';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { dataGroupsClient } from 'common/report/queries/app-api/data-groups-client';
import { DEFAULT_DATA_GROUP } from 'common/widgets/data-group-selector';
import { topEventsStore } from 'common/report/stores/events';
import { eventDefinitionsStore } from 'common/report/stores/event-definitions';
import { fetchGlobalEventPropertyDefinitions, fetchPropertyDefinitionsForEvents, peoplePropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { customPropertiesStore } from 'common/report/stores/custom-properties';
import { TrackingUsageContext } from 'common/report/tracking-setup';
import { DisplaySize } from 'common/components/tooltip/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { DisplayContext as FilterDisplayContext } from 'common/widgets/property-filter-menu/models/types';
import { PropertyFilterFactory } from 'common/widgets/property-filter-menu/models/utils';
import { ProfileHistoryType } from 'common/types/reports/query';
import { SvgIconReact, SvgIconType } from 'common/components/svg-icon';
import { TooltipReact } from 'common/components/tooltip';
import { SelectReact } from 'common/components/select';
import { ButtonSize } from 'common/components/button/constants';
import { SelectTheme } from 'common/components/select/constants';
import { SectionReact } from 'common/components/section';
import { ToggleReact } from 'common/components/toggle';
import { ToggleTheme } from 'common/components/toggle/types';
import { propMenuPrefetchEnabled } from 'common/report/util/rollouts';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { QueryReportTitleReact } from 'common/query-builder/query-report-title';
import { headerParamsToTitleProps } from 'common/query-builder/query-report-title/utils';
import { QueryReportHeaderSave } from 'common/query-builder/query-report-header/types';
import { useTranslation } from 'common/i18n/hooks';
import { QueryBuilderContextProvider } from 'common/contexts/react/query-builder-context';
import { getLocation } from 'common/util/url';
import { Translatable } from 'common/i18n';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { isMetricLevelDataGroupsEnabled } from 'common/report/util/feature-sampling';
import { TopLevelMetricType } from 'common/types/reports/bookmark';
import { getSegmentType, isUserSegment } from '../query-block/segment-menu/types';
import { IncludeCustomPropertyType } from '../query-block/properties-menu/types';
import { isBehaviorSegmentation } from '../query-entry-section/row-types/behavior/utils';
import { BEHAVIOR_MULTI_ATTRIBUTION_FILTER, BEHAVIOR_PROPERTIES, PAGE_DURATION } from '../query-block/properties-menu/constants';
import { QueryEntrySectionReact } from '../query-entry-section';
import { QueryReportHeaderReact } from '../query-report-header';
import { SidebarDataGroupSelector } from '../sidebar-data-group-selector';
import { getEventNamesFromEntries } from '../query-entry-section/row-types/row-util';
import { CAPTURED_TRACKING, TrackingEvent } from './tracking';
import { Filter, QueryBuilderProps, QueryBuilderSelection, ReportSwitchEvent } from './types';
import { sanitizeSelectionForDataGroups, setQueryDataGroup } from './util/data-groups';
import { flattenReferencedEntries, getFunnelStepCount, getMathTypeFromEntry, isMultiAttributionSegment, isXauMathType, MismatchedFilterAndSegment, queryBuilderUtils, sanitizeExclusionSteps, sanitizeFilters, sanitizeGroupBys, sanitizeMultiAttributionFilter, transformFromQuerySegmentEntries } from './util';
import styles from './index.module.styl';
function isCapturedTrackEvent(event: string) {
  return findKey(TrackingEvent, eventName => eventName === event) || CAPTURED_TRACKING.has(event);
}
function normalizeEventEntries(entries: Entry[]) {
  return entries.map(entry => _objectSpread(_objectSpread({}, entry), {}, {
    type: entry.type || ResourceType.Event
  })) as Array<QueryEntry | MetricEntry>;
}
function QueryBuilder(props: QueryBuilderProps) {
  var _props$reportHeaderPa, _props$entries, _props$queryTypeOptio;
  const rootEl = useRef<HTMLDivElement>(null);
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();
  const hasMetricLevelDataGroups = isMetricLevelDataGroupsEnabled(currentProject.get().value);
  const {
    t,
    print
  } = useTranslation();
  const [customEvents, setCustomEvents] = useState<CustomEventDefinition[]>([]);
  const [, setDataGroups] = useState<DataGroup[]>([DEFAULT_DATA_GROUP]);
  const selection: QueryBuilderSelection = {
    attributionSelection: props.breakdownAttributionSelection,
    dataGroupId: props.dataGroupId,
    dateRange: props.dateRange,
    entries: props.entries,
    exclusionSteps: props.exclusionSteps,
    filters: props.filters.map(filter => filter.event),
    filtersOperator: props.filtersOperator,
    groupBy: transformFromQuerySegmentEntries(props.groupBy),
    formulas: props.formulas
  };
  const latestSelection = useRef<QueryBuilderSelection>(selection);
  const entries = normalizeEventEntries(props.entries);
  const exclusionSteps = normalizeEventEntries(props.exclusionSteps);
  const eventNames = useMemo(() => getEventNamesFromEntries(entries), [entries]);
  const attributionSelectorLabeler = useMemo((): ((segment: string) => Translatable) => {
    const {
      groupBy,
      filters
    } = selection;
    let segment = false;
    let filter = false;
    // This is funnels specific logic to determine attribution selection.
    // Oddly, enable-filter-attribution is only used by funnels.
    if (props.enableFilterAttribution) {
      const hasEventBreakdown = groupBy.some(item => {
        var _item$ResourceType$Us;
        return getSegmentType(item) === ResourceType.Event || isUserSegment(item) && ((_item$ResourceType$Us = item[ResourceType.User].profileHistoryOptions) === null || _item$ResourceType$Us === void 0 ? void 0 : _item$ResourceType$Us.type) === ProfileHistoryType.EventTime;
      });
      if (hasEventBreakdown) {
        segment = true;
      }
      const hasEventFilter = filters.some(f => {
        var _f$profileHistoryOpti;
        return f.resourceType === ResourceType.Event || f.resourceType === ResourceType.User && ((_f$profileHistoryOpti = f.profileHistoryOptions) === null || _f$profileHistoryOpti === void 0 ? void 0 : _f$profileHistoryOpti.type) === ProfileHistoryType.EventTime;
      });
      if (hasEventFilter) {
        filter = true;
      }
    } else {
      const validBreakdowns = groupBy.filter(seg => ![ResourceType.Cohort, ResourceType.User].includes(getSegmentType(seg)));
      if (validBreakdowns.length > 0) {
        segment = true;
      }
    }
    if (segment && filter) {
      return seg => _ref => {
        let {
          t
        } = _ref;
        return t("query-builder.segment-selection.segment-and-filter-on", {
          segment: seg
        });
      };
    } else if (segment) {
      return seg => _ref2 => {
        let {
          t
        } = _ref2;
        return t("query-builder.segment-selection.segment-on", {
          segment: seg
        });
      };
    } else if (filter) {
      return seg => _ref3 => {
        let {
          t
        } = _ref3;
        return t("query-builder.segment-selection.filter-on", {
          segment: seg
        });
      };
    }
    return null;
  }, [selection, props.enableFilterAttribution]);

  /**
   * Emits track events that bubble up
   */
  const track = function track(event: string) {
    let trackingProps: UnknownObj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!props.trackName) {
      return;
    }
    if (isCapturedTrackEvent(event)) {
      emitTrackEvent(rootEl.current, {
        eventName: event,
        props: _objectSpread(_objectSpread(_objectSpread({
          project_id: props.projectId,
          tab: props.trackName,
          source: "query-builder"
        }, props.queryTypeOptions ? {
          'query type': props.selectedQueryType
        } : {}), trackingProps), {}, {
          // intentionally put after so it override
          ["usage context"]: TrackingUsageContext.QueryBuilder
        }),
        cancelable: true
      });
    } else {
      throw new Error("tracking event not defined: ".concat(event));
    }
  };

  /** Captures a track event to be tracked as part of this component */
  const captureTracking = ev => {
    const {
      eventPrefix,
      props: trackingProps
    } = ev.detail;
    const eventName = ev.detail.eventName.replace(eventPrefix, "");
    if (isCapturedTrackEvent(eventName)) {
      ev.stopPropagation();
      track(eventName, trackingProps);
    }
  };
  const emitUpdateEvent = function emitUpdateEvent(updates: Partial<QueryBuilderSelection>) {
    var _props$onUpdate;
    let {
      requery = true,
      queryReason = QueryReason.QbOtherUpdate
    }: {
      requery?: boolean;
      queryReason?: QueryReason;
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    // this is to handle an edge case for fast-edit board -> report
    // the event handler may be called BEFORE component initialization (child component store callback)
    let newSelection = _objectSpread(_objectSpread({}, latestSelection.current), updates);
    const originalSegmentLength = newSelection.groupBy.length + newSelection.filters.length;
    const allEntries = flattenReferencedEntries(newSelection.entries, metrics, behaviors);
    newSelection = _objectSpread(_objectSpread({}, newSelection), {}, {
      exclusionSteps: sanitizeExclusionSteps(newSelection.exclusionSteps, newSelection.entries.length),
      groupBy: sanitizeGroupBys({
        entries: newSelection.entries,
        segments: newSelection.groupBy,
        metricsResource: metrics,
        behaviorsResource: behaviors,
        dataGroupId: "dataGroupId" in updates ? updates.dataGroupId : latestSelection.current.dataGroupId,
        hasMetricLevelDataGroups
      })
    });
    if (hasMetricLevelDataGroups) {
      newSelection = sanitizeSelectionForDataGroups({
        query: newSelection,
        customEvents,
        customProperties: customPropertiesStore.value,
        metricsResource: metrics,
        behaviorsResource: behaviors
      });
    } else if (Object.prototype.hasOwnProperty.call(updates, "dataGroupId") && !dataGroupIDsEqual(updates.dataGroupId, latestSelection.current.dataGroupId)) {
      newSelection = setQueryDataGroup({
        query: newSelection,
        dataGroupId: updates.dataGroupId,
        customEvents,
        customProperties: customPropertiesStore.value,
        metricsResource: metrics,
        behaviorsResource: behaviors
      });
    }
    const filters = newSelection.filters.map(filter => PropertyFilterFactory.createPropertyFilter(filter, {
      displayContext: FilterDisplayContext.Default
    }));

    // TODO always sanitize by making tests okay with it
    if (filters.some(filter => isBehaviorSegmentation(filter) || filter.resourceType === ResourceType.Event)) {
      newSelection.filters = sanitizeFilters({
        entries: allEntries,
        filters,
        metricsResource: metrics,
        behaviorsResource: behaviors
      });
    }
    let hasRemovedInlineFilter = false;
    newSelection.entries.forEach(entry => {
      let originalFilter = isMetricEntry(entry) ? entry.behavior.filters : entry.filters;
      if (!originalFilter) originalFilter = [];
      const originalFilterLength = originalFilter.length;
      const sanitizedInlineFilters = sanitizeFilters({
        entries: [entry],
        filters: originalFilter,
        metricsResource: metrics,
        behaviorsResource: behaviors
      });
      const newFilterLength = sanitizedInlineFilters.length;
      if (originalFilterLength !== newFilterLength) {
        hasRemovedInlineFilter = true;
      }
      if (!isMetricEntry(entry)) {
        entry.filters = sanitizedInlineFilters;
      }
    });
    if (hasRemovedInlineFilter || originalSegmentLength !== newSelection.groupBy.length + newSelection.filters.length) {
      console.error("removing breakdown/filter", {
        hasRemovedInlineFilter,
        originalSegmentLength,
        groupByLength: newSelection.groupBy.length,
        filtersLength: newSelection.filters.length
      });
      // href to current location will redirect to the current pre-change clean URL
      addToast({
        message: t("query-builder.incompatible-removed"),
        ctaHref: getLocation().href,
        cta: "Undo"
      });
    }

    // Handle sanitizing multi attribution filters based on breakdown.
    // Make sure to do it after the above toast because we want a unique message
    // indicating the specific incompatibility.
    if (newSelection.groupBy.some(segment => isMultiAttributionSegment(segment))) {
      // Since all the filters are required to share the same property, we just need the last one.
      let lastMismatchedFilterAndSegment: MismatchedFilterAndSegment;
      ({
        results: newSelection.filters,
        mismatchedFilterAndSegment: lastMismatchedFilterAndSegment
      } = sanitizeMultiAttributionFilter(filters, newSelection.groupBy));
      if (lastMismatchedFilterAndSegment) {
        addToast({
          message: t("query-builder.attribution-mismatch", {
            filterName: lastMismatchedFilterAndSegment.filterLabel,
            propertyName: lastMismatchedFilterAndSegment.segmentLabel
          }),
          ctaHref: getLocation().href,
          cta: "Undo"
        });
      }
    }
    latestSelection.current = newSelection;
    (_props$onUpdate = props.onUpdate) === null || _props$onUpdate === void 0 || _props$onUpdate.call(props, _objectSpread(_objectSpread({}, newSelection), {}, {
      requery,
      queryReason,
      delta: updates
    }));
  };

  /**
   * Prefetch property definitions for all selected events
   */
  const prefetchProperties = function prefetchProperties() {
    let refresh = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    // TODO: add data group properties prefetching
    if (refresh) {
      customPropertiesStore.refresh({
        projectId: props.projectId,
        workspaceId: props.workspaceId
      });
    } else {
      customPropertiesStore.fetch({
        projectId: props.projectId,
        workspaceId: props.workspaceId
      });
    }
    const allEventNames = props.entries.map(prop => prop.type === ResourceType.Event ? prop.event.value : null).filter(Boolean);
    if (allEventNames.length > 0) {
      const includeDimensionProperties = !props.excludeDimensionProperties;

      // Prefetch global definitions for tooltips.
      fetchGlobalEventPropertyDefinitions({
        refresh
      });

      // Prefetch definitions for all events. This is used in global query blocks.
      fetchPropertyDefinitionsForEvents(allEventNames, {
        includeDimensionProperties,
        refresh
      });
      for (const event of allEventNames.filter(value => SPECIAL_ALL_EVENT_VALUES.includes(value))) {
        // Prefetch definitions for each events that is not representiive of all events.
        // This is used for inline query blocks.
        fetchPropertyDefinitionsForEvents([event], {
          includeDimensionProperties,
          refresh
        });
      }
    }
  };
  const refreshEventsAndProperties = () => {
    const projectId = props.projectId;
    const workspaceId = props.workspaceId;
    topEventsStore.refresh({
      projectId,
      workspaceId
    });
    eventDefinitionsStore.refresh({
      projectId,
      workspaceId
    });
    peoplePropertyDefinitionsStore.refresh({
      projectId,
      workspaceId
    });
    prefetchProperties(true);
  };
  const fetchProjectData = (projectId: number, workspaceId: number, fetchDataGroups: boolean) => {
    let canceled = false;
    customEventsClient.query({
      projectId,
      workspaceId
    }).then(ces => {
      if (!canceled) {
        setCustomEvents(ces);
      }
    });
    if (fetchDataGroups) {
      dataGroupsClient.query({
        projectId,
        workspaceId
      }).then(dataGroups => {
        if (!canceled) {
          setDataGroups([DEFAULT_DATA_GROUP, ...dataGroups]);
        }
      });
    }
    return () => {
      canceled = true;
    };
  };

  // TODO: this method seems to add unnecessary indirection, these values can be computed directly in the template
  const getPropertyMenuProps = (type: string): QueryEntrySectionProps => {
    const allEntries = flattenReferencedEntries(props.entries, metrics, behaviors);
    const hasEventsOnly = allEntries.every(entry => !entry || isEventBasedEntry(entry) || isGroupedEntry(entry) || isFormulaEntry(entry));
    const hasUserEntriesOnly = allEntries.filter(entry => !isFormulaEntry(entry)).every(entry => isUserBasedEntry(entry));
    const hasCohortMetrics = allEntries.some(entry => entry["type"] === ResourceType.Cohort);
    const hasUserEntriesAndNoCohorts = !hasCohortMetrics && allEntries.some(entry => isUserBasedEntry(entry));
    const isXAU = props.entries.some(entry => isXauMathType(getMathTypeFromEntry(entry)));
    const disabledBehaviors = reason => Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
      acc[key] = reason;
      return acc;
    }, {});
    let dataGroupsEnabled = props.dataGroupsEnabled;
    if (dataGroupsEnabled && type === "breakdown") {
      // In breakdown section, enable data groups if data group id is provided or has events or user entries only
      dataGroupsEnabled = !!props.dataGroupId || hasEventsOnly || hasUserEntriesOnly;
    }

    // Collect unique data group IDs from all metric entries when metric-level data groups is enabled
    let dataGroupIds: Set<string | null>;
    if (hasMetricLevelDataGroups) {
      const allDataGroupIds = props.entries.map(entry => {
        if (entry.type === TopLevelMetricType.NewMetricEntry) {
          var _entry$measurement$da, _entry$measurement, _entry$behavior;
          return (_entry$measurement$da = (_entry$measurement = entry.measurement) === null || _entry$measurement === void 0 ? void 0 : _entry$measurement.dataGroupId) !== null && _entry$measurement$da !== void 0 ? _entry$measurement$da : (_entry$behavior = entry.behavior) === null || _entry$behavior === void 0 ? void 0 : _entry$behavior.dataGroupId;
        } else {
          // legacy entries use the top-level dataGroupId
          return entry.dataGroupId;
        }
      });
      dataGroupIds = new Set(allDataGroupIds.filter(Boolean));
      const hasUserLevelMetrics = allDataGroupIds.some(dgId => !dgId);
      if (hasUserLevelMetrics) {
        dataGroupIds.add(null);
      }
    }
    const attrs = {
      includeCustomProperties: props.includeCustomProperties,
      showBorrowedProperties: props.showBorrowedProperties,
      trackName: props.trackName,
      reportName: props.reportName,
      dataGroupIds
    };
    let otherAttrs;
    if (hasEventsOnly) {
      const types = props.breakdownResourceTypes;
      if (types.length && type === "breakdown") {
        otherAttrs = {
          dataGroupsEnabled: false,
          projectHasCohorts: types.includes(ResourceType.Cohort),
          projectHasPeople: types.includes(ResourceType.User),
          hideEvents: !types.includes(ResourceType.Event),
          disabledItems: !types.includes(ResourceType.Event) || isXAU ? disabledBehaviors(t("query-builder.disabled-behaviors-xau")) : {}
        };
      } else {
        otherAttrs = {
          dataGroupsEnabled,
          projectHasCohorts: props.projectHasCohorts,
          projectHasPeople: props.projectHasPeople,
          hideEvents: false,
          disabledItems: isXAU ? disabledBehaviors(t("query-builder.disabled-behaviors-xau")) : {}
        };
      }
    } else {
      otherAttrs = {
        dataGroupsEnabled,
        projectHasCohorts: props.projectHasCohorts,
        // when combining cohorts & events, only user filtering is possible
        projectHasPeople: (type === "breakdown" ? hasUserEntriesAndNoCohorts : true) && props.projectHasPeople,
        hideEvents: true,
        disabledItems: disabledBehaviors(t("query-builder.disabled-behaviors-cohort"))
      };
    }
    return _objectSpread(_objectSpread({}, attrs), otherAttrs);
  };
  const onSavedCustomEvent = () => emitUpdateEvent({});
  useEffect(() => {
    if (propMenuPrefetchEnabled) {
      prefetchProperties();
    }
  }, []);

  // update project data when project id or workspace id changes
  useEffect(() => {
    const cancel = fetchProjectData(props.projectId, props.workspaceId, props.dataGroupsEnabled);
    return cancel;
  }, [props.projectId, props.workspaceId, props.dataGroupsEnabled]);
  useEffect(() => {
    latestSelection.current = selection;
  }, [selection]);
  const hasNewUI = queryBuilderUtils.isCoreReport() && props.trackName !== "cohorts";
  const isReportSwitchable = Boolean(props.switchableReportName);
  const switchableReportHasQueryOptions = isReportSwitchable && props.queryTypeOptions;
  const isReportSupportedOnDashboard = ["insights", "funnels", "retention", "flows"].includes((_props$reportHeaderPa = props.reportHeaderParams) === null || _props$reportHeaderPa === void 0 ? void 0 : _props$reportHeaderPa.displayContext);
  const showFilterOrOperatorInfo = props.filtersOperator === FiltersOperator.Or;
  const showAttributionSelector = !!attributionSelectorLabeler;
  const stepCount = props.reportName === ReportName.Funnels || props.reportName === ReportName.Insights ? getFunnelStepCount(entries) : undefined;
  const filterProps = getPropertyMenuProps("filter");
  return <div ref={rootEl} elref={props.elref} className={clsx(styles.mpQueryBuilder, props.className, {
    [styles.contained]: props.contained,
    [styles.forNewUi]: hasNewUI,
    [styles.boardsMode]: isReportSupportedOnDashboard,
    [styles.multiAttribution]: props.canUseMultiAttribution
  })} data-sentry-component="QueryBuilder" data-sentry-source-file="index.tsx">
      {/* report header */}
      {props.reportHeaderParams && <ReportHeader boardsMode={isReportSupportedOnDashboard} isSticky={!props.reportHeaderOnly}>
          <QueryReportHeaderReact {...props.reportHeaderParams} onActionclick={ev => {
        var _props$onHeaderAction;
        const action = ev.detail.value;
        if (action === "refresh") {
          refreshEventsAndProperties();
        }
        (_props$onHeaderAction = props.onHeaderActionClick) === null || _props$onHeaderAction === void 0 || _props$onHeaderAction.call(props, action);
      }} onBookmarkselect={ev => {
        var _props$onHeaderBookma;
        return (_props$onHeaderBookma = props.onHeaderBookmarkSelect) === null || _props$onHeaderBookma === void 0 ? void 0 : _props$onHeaderBookma.call(props, ev);
      }} onSave={ev => {
        var _props$onHeaderSave;
        return (_props$onHeaderSave = props.onHeaderSave) === null || _props$onHeaderSave === void 0 ? void 0 : _props$onHeaderSave.call(props, ev);
      }} onReset={ev => {
        var _props$onHeaderReset;
        return (_props$onHeaderReset = props.onHeaderReset) === null || _props$onHeaderReset === void 0 ? void 0 : _props$onHeaderReset.call(props, ev);
      }} />
          <QueryReportTitleReact className={styles.mpQueryReportTitle} {...headerParamsToTitleProps(_objectSpread(_objectSpread({}, props.reportHeaderParams), {}, {
        onSave: (saveParams: QueryReportHeaderSave) => {
          if (!props.onHeaderSave) {
            return;
          }
          const event = new CustomEvent("save", {
            detail: saveParams,
            composed: true
          });
          props.onHeaderSave(event);
        }
      }))} />
        </ReportHeader>}
      {/* skips all the query contents when displaying the report header only */}
      {!props.reportHeaderOnly && <div className={clsx(styles.mpQueryBuilderPanel, styles.forBody, {
      [styles.sectionContentOnly]: !props.headerTitle && props.mainEntryNonCollapsible
    })}>
          {isReportSwitchable && <ToggleReact elref='reportTypeSelector' theme={ToggleTheme.TabBarHeavy} iconOnlyWidthThreshold={430} selected={props.switchableReportName} options={[{
        value: ReportName.Insights,
        icon: "insights-tab",
        iconColor: mpColors["$insights-icon"],
        label: t("mixology.app.insights"),
        tooltipLabel: t("query-builder.switcher.insights")
      }, {
        value: ReportName.Funnels,
        icon: "funnels-tab",
        iconColor: mpColors["$funnels-icon"],
        label: t("mixology.app.funnels"),
        tooltipLabel: t("query-builder.switcher.funnels")
      }, {
        value: ReportName.Flows,
        icon: "flows-tab",
        iconColor: mpColors["$flows-icon"],
        label: t("mixology.app.flows"),
        tooltipLabel: t("query-builder.switcher.flows")
      }, {
        value: ReportName.Retention,
        icon: "retention-tab",
        iconColor: mpColors["$retention-icon"],
        label: t("mixology.app.retention"),
        tooltipLabel: t("query-builder.switcher.retention")
      }]} onSelect={ev => {
        const detail = {
          report: ev.detail.selected as BookmarkType,
          newUrlPath: getReportUrl(ev.detail.selected, {
            projectId: props.projectId,
            workspaceId: props.workspaceId
          })
        };
        const event: ReportSwitchEvent = new CustomEvent("reportswitch", {
          detail,
          bubbles: true,
          composed: true
        });
        rootEl.current.dispatchEvent(event);
      }} />}
          {/* new group analysis selector */}
          {props.dataGroupsEnabled && !props.hideDataGroupsSelector && !props.reportHeaderOnly && !hasMetricLevelDataGroups && <SidebarDataGroupSelector projectId={props.projectId} selected={props.dataGroupId} workspaceId={props.workspaceId} onSelect={dataGroupId => emitUpdateEvent({
        dataGroupId
      })} />}

          <div>
            <SectionReact className={styles.builderSection} headerTitle={hasNewUI || switchableReportHasQueryOptions ? undefined : props.headerTitle} headerTitleLowercase={props.headerTitleLowercase} nonCollapsible={hasNewUI || props.mainEntryNonCollapsible} onTrack={captureTracking}>
              <div className={styles.mpQueryBody}>
                <QueryEntrySectionReact elref='entrySection' autofocus={!entries.length || !entries.every(entry => {
              var _entry$event;
              if (isMetricEntry(entry)) return entry.behavior.name;
              return "value" in ((_entry$event = entry.event) !== null && _entry$event !== void 0 ? _entry$event : {}) && Boolean(entry.event["value"]);
            })} addEntryLabel={props.addEntryLabel} addEntryButtonLabel={props.addEntryButtonLabel} addEntryButtonTooltipText={props.addEntryButtonTooltipText} addEntryMenuWidth={130} allowCohortFilter={props.allowCohortFilter} behaviorMode={props.behaviorMode} canCustomizeNumericBuckets={props.canCustomizeNumericBuckets} canUseBreakdownAttribution={showAttributionSelector} breakdownAttributionLabeler={attributionSelectorLabeler} canUseMultiAttribution={props.canUseMultiAttribution} countUsersOnceState={props.groupBy.length > 0 ? CountUsersOnceState.Enabled : CountUsersOnceState.Disabled} canRenameStep={props.showEntryRename} chartType={props.chartType} disableProfileHistory={props.disableProfileHistory} plotStyle={props.plotStyle} enableEnhancedListFilters={props.enableEnhancedListFilters} enableHideEntry={props.enableHideEntry} enableStickyHeader={["insights", "funnels", "flows"].includes(props.switchableReportName)} groupEventBehavior={props.canGroupEvents ? GroupEventBehaviors.Nest : GroupEventBehaviors.None} dataGroupId={props.dataGroupId} dataGroupsEnabled={props.dataGroupsEnabled} dateRange={props.dateRange} dateUnit={props.dateUnit} disableAddEventFilters={props.disableAddEventFilters} disableAddEntry={props.disableAddEntry} disableAddEntryReason={props.disableAddEntryReason} disableAddGroupedEventReason={props.disableAddGroupedEventReason} disableCloneEntry={props.disableCloneEntry} disableDeleteEntry={props.disableDeleteEntry} disableFirstTimeEventFilters={props.disableFirstTimeEventFilters} entries={entries} minEntries={props.minEntries} emptyPlaceholder={props.emptyPlaceholder} excludeAutotrackEvents={props.excludeAutotrackEvents} excludeCustomEvents={props.excludeCustomEvents} excludeDimensionProperties={props.excludeDimensionProperties} includeCustomProperties={props.includeCustomProperties} showBorrowedProperties={props.showBorrowedProperties} inlineCohortsOverride={props.inlineCohortsOverride} isBlockedPeople={!props.projectHasPeople} maxDataHistoryDays={props.maxDataHistoryDays} metricsMode={props.metricsMode} otherFilterOptions={props.otherFilterOptions.filter(filter => ![BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value, PAGE_DURATION.value].includes(filter.value))} otherAggregationOptions={props.otherAggregationOptions} projectHasPeople={props.projectHasPeople} projectIsFreePlan={props.projectIsFreePlan} hideEditControls={props.hideEditControls} hideEvents projectHasCohorts={props.projectHasCohorts} projectId={props.projectId} queryOrigin={props.queryOrigin} readonly={props.readonlyEntries} restrictTopEventsOnly={props.restrictTopEventsOnly} rowDraggable={props.entriesDraggable} rowIndexToggleable={props.entryIndexToggleable} rowIndexToggleIcon={props.entryIndexToggleIcon as SvgIconType} rowLabelType={props.entryRowLabelType} rowLabelTheme={props.entryRowLabelTheme} enableEventIcon rowLimit={props.rowLimit} rowTypes={props.rowTypes} showAnyEvent={props.showAnyEvent} showAllEvents={props.showAllEvents} showSessions={props.showSessions} showSpecialDatasetProperty={props.showSpecialDatasetProperty} supportB2bCohorts={props.supportB2bCohorts} timeDisplayContext={props.timeDisplayContext} trackName={props.trackName} reportName={props.reportName} trackProperties={_objectSpread({
              sectionName: "entries",
              queryType: props.selectedQueryType
            }, props.trackProperties)} userId={props.userId} workspaceId={props.workspaceId} projectAsOfDate={props.projectAsOfDate} sectionTitle={props.reportName === ReportName.Funnels || props.queryOrigin === "canvas" ? t("query-builder.metric") : props.reportName === ReportName.Insights ? t("query-builder.metric", {
              count: 2
            }) : switchableReportHasQueryOptions ? undefined : props.headerTitle} stagingRow={Boolean(props.stagingRow) && ((_props$entries = props.entries) === null || _props$entries === void 0 ? void 0 : _props$entries.length) === 0} rowDropDisabled={props.reportName === ReportName.Funnels || props.reportName === ReportName.Retention} onSavedCustomEvent={onSavedCustomEvent} onTrack={captureTracking} onUpdate={(_ref4: EntrySectionUpdates<QueryEntry | MetricEntry>) => {
              let {
                entries: updatedEntries,
                savedBehaviorId,
                queryReason,
                requery
              }: EntrySectionUpdates<QueryEntry | MetricEntry> = _ref4;
              return emitUpdateEvent({
                entries: updatedEntries,
                savedBehaviorId
              }, {
                queryReason,
                requery
              });
            }} headerItems={switchableReportHasQueryOptions && props.reportName === ReportName.Retention && <SelectReact className={clsx(styles.mpQueryTypeDropSelection, {
              [styles.isEmpty]: entries.length === 0
            })} elref='queryTypeSelector' theme={SelectTheme.Tertiary} size={ButtonSize.Medium} menuWidth={null} selectedOption={print((_props$queryTypeOptio = props.queryTypeOptions.find(item => item.value === props.selectedQueryType)) === null || _props$queryTypeOptio === void 0 ? void 0 : _props$queryTypeOptio.label)} alwaysShowCaret sections={[{
              items: props.queryTypeOptions.map(item => {
                return {
                  label: item.label,
                  tooltip: {
                    size: DisplaySize.Small,
                    text: item.tooltipLabel
                  },
                  value: item.value
                };
              })
            }]} experimentalPositioning onChange={ev => {
              const queryType: MetricType = (ev.detail as any).selected || ev.detail.item.value;
              emitUpdateEvent({
                queryType
              });
            }} />} footerItems={props.footerItems && <div className={styles.slot}>{props.footerItems}</div>} {...props.metricsMode ? {
              restrictTopEventsOnly: props.restrictTopEventsOnly,
              rowLabelType: RowLabelType.Alpha,
              showAllEvents: true,
              showTopEvents: props.showTopEvents
            } : {}} />
              </div>
            </SectionReact>

            {props.showExclusionSteps && <SectionReact className={styles.builderSection} headerTitle={!hasNewUI && t("query-builder.metrics.exclusion-steps")} isClosed={!hasNewUI && exclusionSteps.length === 0} nonCollapsible={hasNewUI}>
                <div className={styles.mpQueryBody}>
                  <QueryEntrySectionReact elref='exclusionSection' addEntryLabel={props.addExclusionEventLabel} addEntryButtonTooltipText={props.exclusionStepAddEntryTooltipText} chartType={props.chartType} dataGroupId={props.dataGroupId} dataGroupsEnabled={props.dataGroupsEnabled} disableFirstTimeEventFilters={props.disableFirstTimeEventFilters} disableProfileHistory={props.disableProfileHistory} entries={exclusionSteps} rowDraggable entryType={EntryType.ExclusionEvent} excludeAutotrackEvents={props.excludeAutotrackEvents} includeCustomProperties={props.includeCustomProperties} inlineCohortsOverride={props.inlineCohortsOverride} isBlockedPeople={Boolean(props.projectHasPeople)} numExclusionSteps={entries.length} showBorrowedProperties={props.showBorrowedProperties} projectHasPeople={props.projectHasPeople} projectHasCohorts={props.projectHasCohorts} projectId={props.projectId} trackName={props.trackName} trackProperties={_objectSpread(_objectSpread({}, props.trackProperties), {}, {
              sectionName: "exclusion-steps"
            })} workspaceId={props.workspaceId} projectAsOfDate={props.projectAsOfDate} sectionTitle={t("query-builder.metrics.exclusion-steps")} rowLabelType={hasNewUI ? RowLabelType.None : props.entryRowLabelType} onSavedCustomEvent={onSavedCustomEvent} onTrack={captureTracking} onUpdate={(_ref5: EntrySectionUpdates<QueryEventEntry>) => {
              let {
                entries: updatedEntries,
                queryReason,
                requery
              }: EntrySectionUpdates<QueryEventEntry> = _ref5;
              return emitUpdateEvent({
                exclusionSteps: updatedEntries
              }, {
                queryReason,
                requery
              });
            }} />
                </div>
              </SectionReact>}

            {/*allow dynamic sections into the query body*/}
            {props.bodySection && <BodySection onTrack={captureTracking}>{props.bodySection}</BodySection>}
          </div>

          {/* filters */}
          {!props.hideFilter && <SectionReact className={styles.builderSection} elref='filterSectionDropdown' headerTitle={!hasNewUI && t("query-builder.filter", {
        count: 2
      })} isClosed={!props.filters.length && !hasNewUI} nonCollapsible={hasNewUI} onTrack={captureTracking}>
              <div className={styles.mpQueryBody}>
                {showFilterOrOperatorInfo && <FilterOrOperatorInfo />}
                <QueryEntrySectionReact key={"".concat(props.selectedQueryType, "-filters")} elref='filterSection' {...filterProps} dateUnit={props.dateUnit} chartType={props.chartType} dataGroupId={props.dataGroupId} dataGroupsEnabled={filterProps.dataGroupsEnabled && !props.filterHideDataGroups} disableProfileHistory={props.disableProfileHistory} enableEnhancedListFilters={props.enableEnhancedListFilters} enableStickyHeader eventNames={eventNames} entries={props.filters} enableAllDataGroupsForScdProps={props.enableAllDataGroupsForScdProps} excludeDimensionProperties={props.excludeDimensionProperties} inlineCohortsOverride={props.inlineCohortsOverride} otherFilterOptions={props.otherFilterOptions} projectHasPeople={filterProps.projectHasPeople && !props.filterHideUsers} projectId={props.projectId} rowLabelType={RowLabelType.None} rowDraggable rowTypes={[SelectorTypeDefinitions.Filter]} showSpecialDatasetProperty={props.showSpecialDatasetProperty} stepCount={stepCount} supportB2bCohorts={props.supportB2bCohorts} trackProperties={_objectSpread(_objectSpread({}, props.trackProperties), {}, {
            sectionName: "global-filter"
          })} sectionName={"global-filter"} userId={props.userId} workspaceId={props.workspaceId} sectionTitle={t("query-builder.filter")} onSavedCustomEvent={onSavedCustomEvent} onTrack={captureTracking} onUpdate={(_ref6: EntrySectionUpdates<QueryFilterEntry>) => {
            let {
              entries: updatedEntries,
              queryReason
            }: EntrySectionUpdates<QueryFilterEntry> = _ref6;
            // reset board filters since they must stay at the bottom
            const filters: Filter[] = [...updatedEntries.filter(entry => !entry.inheritedFromBoard), ...updatedEntries.filter(entry => entry.inheritedFromBoard)].map(entry => entry.event);
            emitUpdateEvent({
              filters
            }, {
              queryReason
            });
          }} headerItems={hasNewUI && showFilterOrOperatorInfo && <FilterOrOperatorInfo />} />
              </div>
            </SectionReact>}

          {/* breakdowns */}
          {!props.hideGroupBy && <SectionReact className={styles.builderSection} elref='breakdownSectionDropdown' headerTitle={!hasNewUI && t("query-builder.breakdown", {
        count: 2
      })} isClosed={!props.groupBy.length && !hasNewUI} nonCollapsible={hasNewUI} onTrack={captureTracking}>
              <div className={styles.mpQueryBody}>
                {!props.hideGroupBy && <QueryEntrySectionReact key={"".concat(props.selectedQueryType, "-breakdowns")} elref='breakdownSection' {...getPropertyMenuProps("breakdown")} chartType={props.chartType} dateUnit={props.dateUnit} canCustomizeNumericBuckets={props.canCustomizeNumericBuckets} dataGroupId={props.dataGroupId} disableProfileHistory={props.disableProfileHistory} entries={props.groupBy} enableStickyHeader eventNames={eventNames} excludeDimensionProperties={props.excludeDimensionProperties} includeCustomProperties={props.includeCustomProperties} showBorrowedProperties={props.showBorrowedProperties} inlineCohortsOverride={props.inlineCohortsOverride} otherGroupByOptions={props.otherGroupByOptions} projectId={props.projectId} projectIsFreePlan={props.projectIsFreePlan} rowDraggable rowLabelType={RowLabelType.None} rowLimit={props.groupByLimitation} rowTypes={[SelectorTypeDefinitions.Segment]} showSpecialDatasetProperty={props.showSpecialDatasetProperty} stepCount={stepCount} supportB2bCohorts={props.supportB2bCohorts} trackProperties={_objectSpread(_objectSpread({}, props.trackProperties), {}, {
            sectionName: "global-breakdown"
          })} sectionName={"global-breakdown"} userId={props.userId} workspaceId={props.workspaceId} disabled={props.disableBreakdown} sectionTitle={t("query-builder.breakdown")} onSavedCustomEvent={onSavedCustomEvent} onTrack={captureTracking} onUpdate={(_ref7: EntrySectionUpdates<QuerySegmentEntry>) => {
            let {
              entries: updatedEntries,
              queryReason
            }: EntrySectionUpdates<QuerySegmentEntry> = _ref7;
            // reset board breakdowns since they must stay at the bottom
            const groupBy = transformFromQuerySegmentEntries([...updatedEntries.filter(entry => !entry.inheritedFromBoard), ...updatedEntries.filter(entry => entry.inheritedFromBoard)]);
            emitUpdateEvent({
              groupBy
            }, {
              queryReason
            });
          }} />}
              </div>
            </SectionReact>}
        </div>}
    </div>;
}
const DefaultProps: Partial<QueryBuilderProps> = {
  addEntryLabel: "",
  addExclusionEventLabel: "Add exclusion step",
  breakdownAttribution: [],
  breakdownResourceTypes: [],
  entries: [],
  entryRowLabelType: RowLabelType.Numeric,
  includeCustomProperties: IncludeCustomPropertyType.None,
  exclusionSteps: [],
  filters: [],
  filtersOperator: FiltersOperator.And,
  groupBy: [],
  groupByLimitation: null,
  formulas: [],
  formulasDisableReason: "",
  formulasError: null,
  otherGroupByOptions: [],
  otherFilterOptions: [],
  otherAggregationOptions: [],
  rowTypes: [ResourceType.Event],
  entryRowLabelTheme: RowLabelTheme.Report,
  timeDisplayContext: DisplayContext.FullLength,
  trackProperties: {},
  projectAsOfDate: "",
  stagingRow: false
};
QueryBuilder.defaultProps = DefaultProps;
function ReportHeader(props: {
  isSticky: boolean;
  boardsMode: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  return <div ref={ref} className={clsx(styles.mpQueryReportHeader, {
    [styles.boardsMode]: props.boardsMode,
    [styles.isSticky]: props.isSticky
  })} data-sentry-component="ReportHeader" data-sentry-source-file="index.tsx">
      {props.children}
    </div>;
}
function BodySection(props: {
  children: React.ReactNode;
  onTrack: (ev: CustomEvent) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    var _ref$current;
    (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.addEventListener("track", props.onTrack);
    return () => {
      var _ref$current2;
      (_ref$current2 = ref.current) === null || _ref$current2 === void 0 || _ref$current2.removeEventListener("track", props.onTrack);
    };
  }, [props.onTrack]);
  return <div ref={ref} className={clsx(styles.slot, styles.mpQueryBodySections)} data-sentry-component="BodySection" data-sentry-source-file="index.tsx">
      {props.children}
    </div>;
}
function FilterOrOperatorInfo() {
  return <div className={styles.mpQueryFilterOperatorInfo} data-sentry-component="FilterOrOperatorInfo" data-sentry-source-file="index.tsx">
      Matching Any
      <div className={styles.mpQueryFilterOperatorIcon}>
        <SvgIconReact icon='info' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
        <TooltipReact maxWidth='225px' data-sentry-element="TooltipReact" data-sentry-source-file="index.tsx">
          The "Any" operator is deprecated and cannot be changed for this report
        </TooltipReact>
      </div>
    </div>;
}
export const QueryBuilderReact = (props: QueryBuilderProps) => {
  const qbProps = omit(props, ["queryBuilderContextValue"]);
  return <QueryBuilderContextProvider value={props.queryBuilderContextValue || {}} data-sentry-element="QueryBuilderContextProvider" data-sentry-component="QueryBuilderReact" data-sentry-source-file="index.tsx">
      <QueryBuilder {...qbProps} data-sentry-element="QueryBuilder" data-sentry-source-file="index.tsx" />
    </QueryBuilderContextProvider>;
};